import { useMutation, UseMutationOptions } from "@tanstack/react-query";
import axios, { AxiosError } from "axios";

import toast from "components/ui/toast/Toast";
import { UpdateFileStatus } from "../../../../components/content_library/DocumentUploadProgressProvider";

type StoreDocumentVariables = {
  file: File;
  id: string;
  signedUrl: string;
  // Operation the file belongs to, so progress/error updates stay scoped to it.
  operationId?: string | null;
};

/**
 * Uploads a customer document to the signed s3 url retrieved from
 * the @see useCreateCustomerDocuments hook
 * @param options
 * @returns
 */
export function useStoreCustomerDocument(
  updateFileStatus: UpdateFileStatus,
  options?: UseMutationOptions<void, AxiosError, StoreDocumentVariables, unknown>,
) {
  return useMutation<void, AxiosError, StoreDocumentVariables>(
    async ({ signedUrl, file, id, operationId }) => {
      if (!signedUrl) {
        throw new Error("Signed URL is missing");
      }

      if (!file) {
        throw new Error("File is missing");
      }
      await axios.put(signedUrl, file, {
        headers: {
          "Content-Type": "application/octet-stream",
          "Access-Control-Allow-Origin": "*",
        },
        onUploadProgress(progressEvent) {
          if (progressEvent.progress) {
            const uploadProgress = Math.floor(progressEvent.progress * 100);

            updateFileStatus({
              operationId,
              fileName: file.name,
              documentId: id,
              updates: {
                uploadProgress,
                isUploaded: uploadProgress >= 100,
                isProcessed: false,
                isError: false,
              },
            });
          }
        },
      });
    },
    {
      ...options,
      onError: (error, variables, context) => {
        // The upload progress bar only learns about failures from processing-status
        // events, which never arrive for a file that never reached storage — so the
        // toast is the only feedback the user gets for a failed PUT.
        toast.error(`Failed to upload ${variables.file.name}. Please try again.`);
        updateFileStatus({
          operationId: variables.operationId,
          fileName: variables.file.name,
          documentId: variables.id,
          updates: {
            isError: true,
            isFinished: true,
          },
        });
        options?.onError?.(error, variables, context);
      },
    },
  );
}
